import React, {useEffect, useState} from 'react'
import {Formik, Form, Field, useFormikContext, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import SocialMediaModal from '../SocialMediaModal'
import PhoneInput from 'react-phone-input-2'
import {LanguageAutocompleteField} from '../fields/LanguageAutocompleteField'
import {KTSVG, toAbsoluteUrl} from '../../../../../_metronic/helpers'
import CityAutocompleteField from '../../../../modules/auth/components/fields/CityAutocompleteField'
import {getMaxBirthdayDate} from '../../../../utils/dateValidations'
import {handleLettersOnly} from '../../../../utils/inputValidations'
import {DatePicker} from 'antd'
import dayjs from 'dayjs'
import {useHistory} from 'react-router-dom'
import {showErrorAlert} from '../../../../helpers/alert'
import {getConfigNotificationCatalog} from '../../../../services/membersAdminService'
import MemberCategoriesSelect from '../../../members/components/MemberCategoriesSelect'

type SocialMediaItem = {
  id: string
  name: string
  icon: string
  url: string
}

type Props = {
  initialData: any
  onSubmit: (data: any) => void
  isNewRecord: boolean
}

const Step1Schema = Yup.object().shape({
  // introduction_headline: Yup.string().max(220, 'Maximum 220 characters').required('Introduction headline is required'),
  alias_title: Yup.string().max(50, 'Maximum 50 characters'),
  name: Yup.string().max(100, 'Maximum 100 characters').required('Name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  birthday: Yup.string().notRequired().nullable(),
  phone_number: Yup.string().notRequired().nullable(),
  city: Yup.string().notRequired().nullable(),
  notification_ids: Yup.array().of(
    Yup.object({
      id: Yup.number().notRequired(),
      is_configured: Yup.boolean().notRequired(),
    })
  ),
})

const ProfileFormContent = ({
  initialData,
  onSubmit,
  isNewRecord,
}: {
  initialData: any
  onSubmit: (data: any) => void
  isNewRecord: boolean
}) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [countLength, setCountLength] = useState<number>(
    initialData.introduction_headline?.length || 0
  )
  const MAX_LENGTH = 220
  const formik = useFormikContext()
  const history = useHistory()
  const [socialMediaItems, setSocialMediaItems] = useState<SocialMediaItem[]>(
    initialData.social_media_profiles || []
  )
  const [configs, setConfigs] = useState<any[]>([])
  type Assignment = {
    id: number
    is_configured: boolean
  }

  useEffect(() => {
    setSocialMediaItems(initialData.social_media_profiles || [])
    getConfigNotificacions()
  }, [initialData])

  const getConfigNotificacions = async () => {
    let configs_ = await getConfigNotificationCatalog()
    setConfigs(configs_)
    formik.setFieldValue(
      'notification_ids',
      configs_.map((config: any) => ({
        id: config.id,
        is_configured: false,
      }))
    )
  }

  const handleAssignmentToggle = (
    notificationId: number,
    notifications: Assignment[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const exists = notifications.find((item) => item.id === notificationId)

    let updatedNotifications: Assignment[]

    if (exists) {
      updatedNotifications = notifications.map((item) =>
        item.id === notificationId
          ? {
              ...item,
              is_configured: !item.is_configured,
            }
          : item
      )
    } else {
      updatedNotifications = [
        ...notifications,
        {
          id: notificationId,
          is_configured: true,
        },
      ]
    }

    setFieldValue('notification_ids', updatedNotifications)
  }

  const handleSocialMediaSelect = (option: any) => {
    const exists = formik.values.social_media_profiles.some(
      (item: SocialMediaItem) => item.id === option.id
    )
    if (exists) {
      showErrorAlert({title: '', message: 'You already added this social media.'})
      return
    }

    const newItem: SocialMediaItem = {
      id: option.id,
      name: option.name,
      icon: option.icon,
      url: '',
    }

    const updated = [...formik.values.social_media_profiles, newItem]
    formik.setFieldValue('social_media_profiles', updated)
    setSocialMediaItems(updated)
  }

  const handleSocialMediaUrlChange = (id: string, url: string) => {
    const updated = formik.values.social_media_profiles.map((item: SocialMediaItem) =>
      item.id === id ? {...item, url} : item
    )

    formik.setFieldValue('social_media_profiles', updated)
    setSocialMediaItems(updated)
  }

  const handleOnSelectLanguages = (languages: string[]) => {
    formik.setFieldValue('language_spoken', languages)
  }

  const handleRemoveSocialMedia = (id: string) => {
    const updated = formik.values.social_media_profiles.filter(
      (item: SocialMediaItem) => item.id !== id
    )

    formik.setFieldValue('social_media_profiles', updated)
    setSocialMediaItems(updated)
  }

  return (
    <Form className='profile-card'>
      {/* Introduction */}
      <div className='profile-form-group'>
        <label>Introduction headline</label>
        <Field name='introduction_headline'>
          {({field, form}: any) => (
            <input
              {...field}
              maxLength={MAX_LENGTH}
              placeholder='Type your introduction headline here'
              onChange={(e) => {
                form.setFieldValue(field.name, e.target.value)
                setCountLength(e.target.value.length)
              }}
            />
          )}
        </Field>
        {/* <ErrorMessage name='introduction_headline' component='div' className="tap-add-experience-text text-danger" /> */}
        <small>
          {countLength} / {MAX_LENGTH}
        </small>
      </div>

      {/* Alias */}
      <div className='profile-form-group'>
        <label>Alias / Title (if applicable)</label>
        <Field name='alias_title' />
        <ErrorMessage
          name='alias_title'
          component='div'
          className='tap-add-experience-text text-danger'
        />
      </div>

      {/* Name + Email */}
      <div className='profile-form-row'>
        <div className='profile-form-group'>
          <label>Name</label>
          <Field name='name' onKeyPress={handleLettersOnly} />
          <ErrorMessage
            name='name'
            component='div'
            className='tap-add-experience-text text-danger'
          />
        </div>

        <div className='profile-form-group'>
          <label>Email</label>
          <Field name='email' type='email' readOnly={!isNewRecord} />
          <ErrorMessage
            name='email'
            component='div'
            className='tap-add-experience-text text-danger'
          />
          <small>This information will not be displayed to public</small>
        </div>
      </div>

      {/* Birthday + Phone */}
      <div className='profile-form-row'>
        <div className='profile-form-group profile-form-date'>
          <label>Date of birth</label>
          <DatePicker
            className='underline-picker'
            format='YYYY-MM-DD'
            placeholder='YYYY-MM-DD'
            defaultPickerValue={dayjs().subtract(18, 'year')}
            disabledDate={(current) =>
              current && current.isAfter(dayjs().subtract(18, 'year').endOf('day'))
            }
            value={formik.values.birthday ? dayjs(formik.values.birthday, 'YYYY-MM-DD') : null}
            onChange={(value) =>
              formik.setFieldValue('birthday', value ? value.format('YYYY-MM-DD') : null)
            }
          />
          <ErrorMessage
            name='birthday'
            component='div'
            className='tap-add-experience-text text-danger'
          />
          <small>This information will not be displayed to public</small>
        </div>

        <div className='profile-form-group'>
          <label>Phone</label>
          <div className='profile-input-wrapper'>
            <Field name='phone_number'>
              {({field, form, meta}: any) => (
                <>
                  <PhoneInput
                    country='us'
                    placeholder='+1'
                    containerClass='profile-input'
                    value={field.value || ''}
                    onChange={(val: string) => form.setFieldValue(field.name, val)}
                    onBlur={() => form.setFieldTouched(field.name, true)}
                  />
                </>
              )}
            </Field>
          </div>
          <ErrorMessage
            name='phone_number'
            component='div'
            className='tap-add-experience-text text-danger'
          />
          <small>This information will not be displayed to public</small>
        </div>
      </div>

      {/* Preferred Contact */}
      <div className='profile-form-group'>
        <label>Preferred Contact Method</label>
        <div className='profile-checkbox-row'>
          <label className='profile-checkbox'>
            <Field type='checkbox' name='prefered_email' id='preferred_email' />
            <span className='profile-checkbox-box'></span>
          </label>
          <label htmlFor='preferred_email'>Email</label>

          <label className='profile-checkbox'>
            <Field type='checkbox' name='preferred_phone' id='preferred_phone' />
            <span className='profile-checkbox-box'></span>
          </label>
          <label htmlFor='preferred_phone'>Phone</label>
        </div>
      </div>

      {/* Languages */}
      <div className='profile-form-group'>
        <label>Language spoken</label>
        <LanguageAutocompleteField
          values={formik.values.language_spoken}
          onChange={handleOnSelectLanguages}
        />
      </div>

      {/* PR-I-79: Member Taxonomy — self-select categories oculto por decision de
          producto: por ahora la asignacion de categorias es exclusiva del staff
          (MemberCategoriesAdmin). Se deja el componente montable para reactivar
          rapido si mas adelante se abre a los miembros. */}
      {/* <div className='profile-form-group'>
        <MemberCategoriesSelect />
      </div> */}

      {/* Social Media */}
      <div className='profile-form-group social-media-group'>
        <label>Social Media & Online Presence</label>

        {socialMediaItems.map((item) => (
          <div key={item.id} className='social-media-input'>
            <div className='social-media-input-content'>
              <KTSVG path={toAbsoluteUrl(item.icon)} className='social-media-input-icon' />
              <input
                type='text'
                placeholder={`Add your ${item.name.toLowerCase()} profile URL`}
                value={item.url}
                onChange={(e) => handleSocialMediaUrlChange(item.id, e.target.value)}
                className='social-media-input-field'
              />
              <button
                type='button'
                onClick={() => handleRemoveSocialMedia(item.id)}
                className='remove-btn tap-flex-center'
              >
                <KTSVG path='/media/svg/nobilis/teams_and_partner/team_and_partner_experiences_add_experience_availability_date_delete.svg' />
                &nbsp;
              </button>
            </div>
          </div>
        ))}

        <div className='social-media-add-btn' onClick={() => setModalOpen(true)}>
          {socialMediaItems.length > 0 ? '+ Add Another' : '+ Add Social Media & Online Presence'}
        </div>

        <SocialMediaModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          onSelect={handleSocialMediaSelect}
        />
      </div>

      {/* City */}
      <div className='profile-form-group'>
        <label>City & Country</label>
        <CityAutocompleteField name='city' />
        <ErrorMessage name='city' component='div' className='tap-add-experience-text text-danger' />
      </div>

      {/* notificacion */}
      <div className='w-100 gap-3'>
        <div className='assignment-title mb-4'>Mailing Configuration</div>
        <div className='w-100'>
          {(configs ?? []).map((config: any, index: number) => {
            return (
              <div className='pf-checkbox mb-2' key={`notification-config-${index}-key`}>
                <input
                  type='checkbox'
                  id={`notification-config-${index}`}
                  className='pf-check'
                  checked={formik.values.notification_ids.some(
                    (item: Assignment) => item.id === config.id && item.is_configured
                  )}
                  onChange={() =>
                    handleAssignmentToggle(
                      config.id,
                      formik.values.notification_ids,
                      formik.setFieldValue
                    )
                  }
                />
                <label
                  htmlFor={`notification-config-${index}`}
                  className='pf-checkbox-content tap-flex-center'
                >
                  {/* <div className="assignment-title">{config?.name}</div> */}
                  <div className='assignment-description mt-1'>{config?.description}</div>
                </label>
              </div>
            )
          })}
        </div>
      </div>

      <div className='w-100 d-flex flex-row justify-content-between gap-5'>
        {/* back */}
        <div
          className='tap-add-experience-2-btn-main w-50'
          role='button'
          onClick={() => history.goBack()}
        >
          <div className='tap-host-button-text tap-flex-center'>
            Go back &nbsp;
            <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
          </div>
        </div>

        {/* Submit */}
        <button type='submit' className='btn nb-btn-outline w-50'>
          <span className='nb-heading-md'>continue</span>
          <img
            src='/media/svg/nobilis/vector1.svg'
            alt=''
            className='nb-btn-icon nb-btn-icon--black'
          />
        </button>
      </div>
    </Form>
  )
}

export default function ProfileStep1({initialData, onSubmit, isNewRecord}: Props) {
  return (
    <Formik
      initialValues={{
        introduction_headline: initialData.introduction_headline || '',
        alias_title: initialData.alias_title || '',
        name: initialData.name || '',
        email: initialData.email || '',
        birthday: initialData.birthday || '',
        phone_number: initialData.phone_number || '',
        prefered_email: initialData.prefered_email || false,
        preferred_phone: initialData.preferred_phone || false,
        city: initialData.city || '',
        language_spoken: initialData.language_spoken || [],
        social_media_profiles: initialData.social_media_profiles || [],
        notification_ids: [],
      }}
      enableReinitialize
      validationSchema={Step1Schema}
      onSubmit={(values) => {
        onSubmit(values)
      }}
    >
      {() => (
        <ProfileFormContent
          initialData={initialData}
          onSubmit={onSubmit}
          isNewRecord={isNewRecord}
        />
      )}
    </Formik>
  )
}
